import {
  cn,
  convertNumber,
  getUrlBasedOnType,
} from '@/helpers/utils/functionality-utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { staffDActions } from '@/store/slices/main/dashboard/staffDashboardMyTaskSlice';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { useIntersectionObserver } from 'primereact/hooks';
import type { OverlayPanel } from 'primereact/overlaypanel';
import { ScrollPanel } from 'primereact/scrollpanel';
import { useEffect, useRef, type RefObject } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Link } from 'react-router';

export const AlarmsAsPopover = ({
  isFetching,
  overlayRef,
}: {
  isFetching: boolean;
  overlayRef: RefObject<OverlayPanel | null>;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('tasks');

  const cachedAlarms = useAppSelector(
    (state) => state.staffDashboard.myAlarmsState.alarms,
  );

  const first5CachedAlarms = cachedAlarms.slice(0, 5);

  const elementRef = useRef<HTMLDivElement>(null);
  //@ts-expect-error works
  const visible = useIntersectionObserver(elementRef, { threshold: 0.5 });

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        const first5CachedAlarms = cachedAlarms.slice(0, 5);
        const setRead = first5CachedAlarms.map((notif) => notif.alarm_id);
        dispatch(staffDActions.setAsReadAlarms(setRead));
      }, 3000);
    }
  }, [visible]);

  return (
    <div
      ref={elementRef}
      className="flex size-full flex-col justify-between gap-1"
    >
      <div className="relative flex grow flex-col gap-4">
        <div className="-mx-5 -mt-5 flex items-center justify-between gap-3 px-10 py-3 text-2xl font-semibold shadow-[0_2px_6px_-2px_rgba(0,0,0,0.5)]">
          {t('alarms')}
          {isFetching ? (
            <i className="pi pi-spin pi-spinner"></i>
          ) : (
            <Chip
              className="size-8 shrink-0"
              template={
                <div className="grid size-full place-items-center">
                  {convertNumber(first5CachedAlarms.length) + ''}
                </div>
              }
            />
          )}
        </div>
        <RemoveScroll className="flex size-full grow flex-col text-lg">
          <ScrollPanel style={{ height: '330px' }}>
            {first5CachedAlarms.map((alarm, i) => {
              const moreInfo = alarm.more_info;

              const firstKey = Object.keys(moreInfo)?.[0];
              const firstId = firstKey ? Number(moreInfo[firstKey]) : null;

              let url = '';
              if (firstKey && firstId) {
                url = getUrlBasedOnType(firstKey, firstId);
              }

              return (
                <div
                  key={alarm.alarm_id}
                  className={cn(
                    'flex gap-3 border-b last:border-transparent',
                    i === cachedAlarms.length - 1 && 'border-transparent',
                  )}
                >
                  <div className="w-[30px] shrink-0">
                    <i
                      className={cn(
                        'pi ml-3 mt-5 text-xs',
                        alarm.isNew
                          ? 'pi-circle-fill text-red-500'
                          : 'pi-check text-surface-400',
                      )}
                    ></i>
                  </div>
                  <div
                    className={cn(
                      'flex flex-col py-3',
                      i === cachedAlarms.length - 1 && 'border-transparent',
                    )}
                  >
                    <div className="flex grow flex-col gap-2">
                      {url ? (
                        <div className="flex items-center gap-2">
                          <Link
                            onClick={(e) => {
                              overlayRef.current?.toggle(e);
                            }}
                            to={url}
                          >
                            <div className="text-surface-600 underline underline-offset-4 hover:text-blue-600">
                              {alarm.message}
                            </div>
                          </Link>
                        </div>
                      ) : (
                        alarm.message
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </ScrollPanel>
        </RemoveScroll>
      </div>
      <div className="-mx-5 -mb-5 flex h-16 items-center justify-between shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.5)]">
        <Link
          onClick={(e) => overlayRef.current?.toggle(e)}
          to={'/main/dashboard?t=2'}
        >
          <Button
            type="button"
            severity="info"
            className="h-10 w-fit text-indigo-500 shadow-none shadow-indigo-500"
            link
          >
            {t('viewAllAlarms')}
          </Button>
        </Link>
      </div>
    </div>
  );
};
